import { useEffect, useRef, useState } from "react";
import api, { resolveAssetUrl } from "../../lib/api";
import { currency } from "../../lib/status";

const EMPTY_SVC = {
    name: "",
    description: "",
    base_price: "",
    pricing_type: "fixed",
    duration_minutes: 60,
    hourly_rate: "",
    inspection_fee: "",
    image_url: "",
};

const EMPTY_CAT = {
    name: "",
    slug: "",
    icon: "wrench",
    description: "",
    image_url: "",
    order: 0,
};

const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const inputCls =
    "w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm focus:border-[#D4AF37] focus:outline-none";

// ImageUpload — drop-in field that supports either an image URL or a local file.
function ImageUpload({ value, onChange, testid }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const inputRef = useRef(null);

    const pick = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return setErr("Max 5 MB.");
        setBusy(true);
        setErr("");
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div className="lx-label text-white/50 mb-1">Image</div>
            <div className="flex gap-2 items-center">
                {value && (
                    <img
                        src={resolveAssetUrl(value)}
                        alt=""
                        className="w-16 h-16 object-cover border border-[#2B1438]"
                    />
                )}
                <div className="flex-1 space-y-2">
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Paste URL or upload below"
                        className={inputCls}
                        data-testid={`${testid}-url`}
                    />
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            onChange={(e) => pick(e.target.files?.[0])}
                            className="hidden"
                            data-testid={`${testid}-file`}
                        />
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#2B1438] text-white/70 hover:border-[#D4AF37] disabled:opacity-50"
                            data-testid={`${testid}-pick`}
                        >
                            {busy ? "Uploading…" : "Upload from device"}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-red-500/40 text-red-300"
                                data-testid={`${testid}-clear`}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {err && (
                <div className="text-xs text-red-300 mt-1">{err}</div>
            )}
        </div>
    );
}

export default function AdminServices() {
    const [cats, setCats] = useState([]);
    const [services, setServices] = useState([]);
    const [activeCat, setActiveCat] = useState(null);

    // Service add/edit/delete state
    const [svcModal, setSvcModal] = useState(null); // null | { mode: "add"|"edit", form, id? }
    const [svcDelete, setSvcDelete] = useState(null);
    const [svcError, setSvcError] = useState("");

    // Category modal state
    const [catModalOpen, setCatModalOpen] = useState(false);
    const [catForm, setCatForm] = useState(null); // null = closed; { mode, form, id? } when open
    const [catDelete, setCatDelete] = useState(null);
    const [catError, setCatError] = useState("");

    const [busy, setBusy] = useState(false);

    const loadCats = async () => {
        const { data: c } = await api.get("/categories");
        setCats(c);
        if (c.length && !activeCat) setActiveCat(c[0].id);
        return c;
    };

    const loadServices = (catId) => {
        if (!catId) return;
        api.get("/services", { params: { category_id: catId } })
            .then(({ data }) => setServices(data));
    };

    useEffect(() => { loadCats(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { loadServices(activeCat); }, [activeCat]);

    // --- Service handlers ---
    const openAddService = () => {
        setSvcError("");
        setSvcModal({ mode: "add", form: { ...EMPTY_SVC } });
    };
    const openEditService = (s) => {
        setSvcError("");
        setSvcModal({
            mode: "edit",
            id: s.id,
            form: {
                name: s.name || "",
                description: s.description || "",
                base_price: String(s.base_price ?? ""),
                pricing_type: s.pricing_type || "fixed",
                duration_minutes: s.duration_minutes ?? 60,
                hourly_rate: String(s.hourly_rate ?? ""),
                inspection_fee: String(s.inspection_fee ?? ""),
                image_url: s.image_url || "",
            },
        });
    };

    const saveService = async () => {
        if (!svcModal) return;
        const { mode, id, form } = svcModal;
        if (!form.name.trim()) return setSvcError("Name is required.");
        const price = Number(form.base_price);
        if (!price || price <= 0) return setSvcError("Base price must be greater than 0.");
        setBusy(true); setSvcError("");
        const body = {
            name: form.name.trim(),
            description: form.description,
            base_price: price,
            pricing_type: form.pricing_type,
            duration_minutes: Number(form.duration_minutes) || 60,
            hourly_rate: Number(form.hourly_rate) || 0,
            inspection_fee: Number(form.inspection_fee) || 0,
            image_url: form.image_url || null,
        };
        try {
            if (mode === "add") {
                if (!activeCat) throw new Error("Pick a category first.");
                await api.post("/services", { category_id: activeCat, ...body });
            } else {
                await api.patch(`/services/${id}`, body);
            }
            setSvcModal(null);
            loadServices(activeCat);
        } catch (e) {
            setSvcError(e?.response?.data?.detail || e.message || "Save failed.");
        } finally { setBusy(false); }
    };

    const doDeleteService = async () => {
        if (!svcDelete) return;
        setBusy(true); setSvcError("");
        try {
            const { data } = await api.delete(`/services/${svcDelete.id}`);
            setSvcDelete(null);
            loadServices(activeCat);
            // eslint-disable-next-line no-alert
            alert(data?.message || "Service deleted.");
        } catch (e) {
            setSvcError(e?.response?.data?.detail || "Delete failed.");
        } finally { setBusy(false); }
    };

    // --- Category handlers ---
    const openAddCategory = () => {
        setCatError("");
        setCatForm({ mode: "add", form: { ...EMPTY_CAT } });
    };
    const openEditCategory = (c) => {
        setCatError("");
        setCatForm({
            mode: "edit",
            id: c.id,
            form: {
                name: c.name || "",
                slug: c.slug || "",
                icon: c.icon || "wrench",
                description: c.description || "",
                image_url: c.image_url || "",
                order: c.order ?? 0,
            },
        });
    };

    const saveCategory = async () => {
        if (!catForm) return;
        const { mode, id, form } = catForm;
        if (!form.name.trim()) return setCatError("Name is required.");
        const slug = form.slug.trim() || slugify(form.name);
        if (!slug) return setCatError("Slug could not be derived. Provide a slug.");
        setBusy(true); setCatError("");
        const body = {
            name: form.name.trim(),
            slug,
            icon: form.icon || "wrench",
            description: form.description || null,
            image_url: form.image_url || null,
            order: Number(form.order) || 0,
        };
        try {
            if (mode === "add") {
                const { data } = await api.post("/categories", body);
                setCatForm(null);
                const list = await loadCats();
                // Select the new category
                if (data?.id) setActiveCat(data.id);
                // ensure list state seen
                if (list.length && !list.find((c) => c.id === activeCat)) {
                    /* noop */
                }
            } else {
                await api.patch(`/categories/${id}`, body);
                setCatForm(null);
                await loadCats();
            }
        } catch (e) {
            setCatError(e?.response?.data?.detail || "Save failed.");
        } finally { setBusy(false); }
    };

    const doDeleteCategory = async () => {
        if (!catDelete) return;
        setBusy(true); setCatError("");
        try {
            const { data } = await api.delete(`/categories/${catDelete.id}`);
            setCatDelete(null);
            const list = await loadCats();
            // Reset active if the deleted one was active
            if (activeCat === catDelete.id) {
                setActiveCat(list[0]?.id || null);
            }
            // eslint-disable-next-line no-alert
            alert(data?.message || "Category deleted.");
        } catch (e) {
            setCatError(e?.response?.data?.detail || "Delete failed.");
        } finally { setBusy(false); }
    };

    const svcM = svcModal;
    const catM = catForm;

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Catalogue</div>
                    <h1 className="font-serif text-4xl text-white">Services</h1>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setCatModalOpen(true)}
                        data-testid="adm-cat-manage"
                        className="px-4 py-2 text-xs uppercase tracking-wider border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    >
                        Manage categories
                    </button>
                    <button
                        onClick={openAddService}
                        data-testid="adm-svc-add-open"
                        disabled={!activeCat}
                        className="px-4 py-2 text-xs uppercase tracking-wider bg-[#D4AF37] text-[#180A21] hover:bg-[#E8D399] disabled:opacity-40"
                    >
                        + Add Service
                    </button>
                </div>
            </div>

            <div className="flex gap-2 overflow-x-auto lx-scroll">
                {cats.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setActiveCat(c.id)}
                        data-testid={`adm-cat-${c.slug}`}
                        className={`shrink-0 px-4 py-2 text-xs uppercase tracking-wider border whitespace-nowrap ${
                            activeCat === c.id
                                ? "bg-[#D4AF37] text-[#180A21] border-[#D4AF37]"
                                : "border-[#2B1438] text-white/60"
                        }`}
                    >
                        {c.name}
                    </button>
                ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((s) => (
                    <div
                        key={s.id}
                        className="lx-card-dark overflow-hidden"
                        data-testid={`adm-svc-${s.id}`}
                    >
                        {s.image_url && (
                            <div className="aspect-[16/9] overflow-hidden">
                                <img
                                    src={resolveAssetUrl(s.image_url)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                        <div className="p-5">
                            <div className="lx-label text-[#D4AF37] mb-2">
                                {s.pricing_type} · {s.duration_minutes} min
                            </div>
                            <div className="font-serif text-xl text-white mb-3">{s.name}</div>
                            {s.description && (
                                <div className="text-xs text-white/60 mb-3 line-clamp-2">
                                    {s.description}
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <div className="font-serif text-2xl text-[#D4AF37]">
                                    {currency(s.base_price, "AED")}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openEditService(s)}
                                        data-testid={`adm-svc-edit-${s.id}`}
                                        className="text-xs uppercase tracking-wider px-3 py-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => setSvcDelete({ id: s.id, name: s.name })}
                                        data-testid={`adm-svc-delete-${s.id}`}
                                        className="text-xs uppercase tracking-wider px-3 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- Delete service confirm --- */}
            {svcDelete && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="adm-svc-delete-modal">
                    <div className="lx-card-dark max-w-md w-full p-6">
                        <div className="lx-label text-[#D4AF37] mb-2">Confirm deletion</div>
                        <div className="font-serif text-xl text-white mb-3">Delete “{svcDelete.name}”?</div>
                        <div className="text-xs text-white/60 mb-5 leading-relaxed">
                            If this service has no existing bookings it will be permanently removed.
                            Otherwise it will be marked inactive so old bookings keep their original details.
                            Customers and technicians will no longer see it.
                        </div>
                        {svcError && (
                            <div className="text-xs text-red-300 border border-red-500/40 bg-red-500/10 px-3 py-2 mb-4">{svcError}</div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setSvcDelete(null); setSvcError(""); }} disabled={busy} data-testid="adm-svc-delete-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60">Cancel</button>
                            <button onClick={doDeleteService} disabled={busy} data-testid="adm-svc-delete-confirm" className="px-4 py-2 text-xs uppercase tracking-wider bg-red-500 text-white disabled:opacity-50">{busy ? "Deleting…" : "Delete"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Service add/edit modal --- */}
            {svcM && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="adm-svc-add-modal">
                    <div className="lx-card-dark max-w-lg w-full p-6 my-8">
                        <div className="lx-label text-[#D4AF37] mb-2">{svcM.mode === "add" ? "New service" : "Edit service"}</div>
                        <div className="font-serif text-2xl text-white mb-1">{svcM.mode === "add" ? "Add a service" : "Update service"}</div>
                        <div className="text-xs text-white/60 mb-5">Category: <span className="text-white/90">{cats.find((c) => c.id === activeCat)?.name || "—"}</span></div>

                        <div className="space-y-3">
                            <div>
                                <div className="lx-label text-white/50 mb-1">Name</div>
                                <input value={svcM.form.name} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, name: e.target.value } })} data-testid="adm-svc-add-name" placeholder="e.g. AC Deep Cleaning (1.5 Ton)" className={inputCls} />
                            </div>
                            <div>
                                <div className="lx-label text-white/50 mb-1">Description</div>
                                <textarea value={svcM.form.description} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, description: e.target.value } })} data-testid="adm-svc-add-description" rows={2} className={inputCls} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Base price (AED)</div>
                                    <input type="number" value={svcM.form.base_price} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, base_price: e.target.value } })} data-testid="adm-svc-add-price" className={inputCls} />
                                </div>
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Duration (min)</div>
                                    <input type="number" value={svcM.form.duration_minutes} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, duration_minutes: e.target.value } })} data-testid="adm-svc-add-duration" className={inputCls} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Type</div>
                                    <select value={svcM.form.pricing_type} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, pricing_type: e.target.value } })} data-testid="adm-svc-add-type" className={inputCls}>
                                        <option value="fixed">Fixed</option>
                                        <option value="hourly">Hourly</option>
                                        <option value="inspection">Inspection</option>
                                    </select>
                                </div>
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Hourly rate</div>
                                    <input type="number" value={svcM.form.hourly_rate} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, hourly_rate: e.target.value } })} data-testid="adm-svc-add-hourly" className={inputCls} />
                                </div>
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Inspection fee</div>
                                    <input type="number" value={svcM.form.inspection_fee} onChange={(e) => setSvcModal({ ...svcM, form: { ...svcM.form, inspection_fee: e.target.value } })} data-testid="adm-svc-add-inspection" className={inputCls} />
                                </div>
                            </div>
                            <ImageUpload value={svcM.form.image_url} onChange={(v) => setSvcModal({ ...svcM, form: { ...svcM.form, image_url: v } })} testid="adm-svc-add-image" />
                        </div>

                        {svcError && (
                            <div className="mt-4 text-xs text-red-300 border border-red-500/40 bg-red-500/10 px-3 py-2">{svcError}</div>
                        )}

                        <div className="flex gap-2 justify-end mt-6">
                            <button onClick={() => { setSvcModal(null); setSvcError(""); }} disabled={busy} data-testid="adm-svc-add-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60">Cancel</button>
                            <button onClick={saveService} disabled={busy} data-testid="adm-svc-add-submit" className="px-4 py-2 text-xs uppercase tracking-wider bg-[#D4AF37] text-[#180A21] hover:bg-[#E8D399] disabled:opacity-50">{busy ? "Saving…" : svcM.mode === "add" ? "Create service" : "Save changes"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Categories manage modal (list + add/edit/delete entries) --- */}
            {catModalOpen && !catM && !catDelete && (
                <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="adm-cat-manage-modal">
                    <div className="lx-card-dark max-w-2xl w-full p-6 my-8">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="lx-label text-[#D4AF37] mb-1">Catalogue</div>
                                <div className="font-serif text-2xl text-white">Manage categories</div>
                            </div>
                            <button onClick={openAddCategory} data-testid="adm-cat-add-open" className="px-3 py-2 text-xs uppercase tracking-wider bg-[#D4AF37] text-[#180A21] hover:bg-[#E8D399]">+ Add</button>
                        </div>

                        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                            {cats.length === 0 && <div className="text-xs text-white/40 text-center py-6">No categories yet.</div>}
                            {cats.map((c) => (
                                <div key={c.id} className="flex items-center gap-3 p-3 border border-[#2B1438] bg-[#0F0614]" data-testid={`adm-cat-row-${c.slug}`}>
                                    {c.image_url ? (
                                        <img src={resolveAssetUrl(c.image_url)} alt="" className="w-12 h-12 object-cover border border-[#2B1438]" />
                                    ) : (
                                        <div className="w-12 h-12 bg-[#180A21] border border-[#2B1438] flex items-center justify-center text-[#D4AF37] text-xs">{c.icon || "•"}</div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white font-medium truncate">{c.name}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-white/40 truncate">{c.slug} · order {c.order ?? 0}</div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => openEditCategory(c)} data-testid={`adm-cat-edit-${c.slug}`} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10">Edit</button>
                                        <button onClick={() => setCatDelete({ id: c.id, name: c.name })} data-testid={`adm-cat-delete-${c.slug}`} className="text-xs uppercase tracking-wider px-3 py-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10">Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end mt-5">
                            <button onClick={() => { setCatModalOpen(false); setCatError(""); }} data-testid="adm-cat-manage-close" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Category add/edit modal --- */}
            {catM && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="adm-cat-form-modal">
                    <div className="lx-card-dark max-w-lg w-full p-6 my-8">
                        <div className="lx-label text-[#D4AF37] mb-2">{catM.mode === "add" ? "New category" : "Edit category"}</div>
                        <div className="font-serif text-2xl text-white mb-5">{catM.mode === "add" ? "Add a category" : "Update category"}</div>

                        <div className="space-y-3">
                            <div>
                                <div className="lx-label text-white/50 mb-1">Name</div>
                                <input value={catM.form.name} onChange={(e) => setCatForm({ ...catM, form: { ...catM.form, name: e.target.value, slug: catM.form.slug || slugify(e.target.value) } })} data-testid="adm-cat-name" placeholder="e.g. Plumbing" className={inputCls} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Slug</div>
                                    <input value={catM.form.slug} onChange={(e) => setCatForm({ ...catM, form: { ...catM.form, slug: slugify(e.target.value) } })} data-testid="adm-cat-slug" className={inputCls} />
                                </div>
                                <div>
                                    <div className="lx-label text-white/50 mb-1">Icon (string)</div>
                                    <input value={catM.form.icon} onChange={(e) => setCatForm({ ...catM, form: { ...catM.form, icon: e.target.value } })} data-testid="adm-cat-icon" className={inputCls} />
                                </div>
                            </div>
                            <div>
                                <div className="lx-label text-white/50 mb-1">Description</div>
                                <textarea value={catM.form.description} onChange={(e) => setCatForm({ ...catM, form: { ...catM.form, description: e.target.value } })} data-testid="adm-cat-description" rows={2} className={inputCls} />
                            </div>
                            <div>
                                <div className="lx-label text-white/50 mb-1">Order</div>
                                <input type="number" value={catM.form.order} onChange={(e) => setCatForm({ ...catM, form: { ...catM.form, order: e.target.value } })} data-testid="adm-cat-order" className={inputCls} />
                            </div>
                            <ImageUpload value={catM.form.image_url} onChange={(v) => setCatForm({ ...catM, form: { ...catM.form, image_url: v } })} testid="adm-cat-image" />
                        </div>

                        {catError && (
                            <div className="mt-4 text-xs text-red-300 border border-red-500/40 bg-red-500/10 px-3 py-2">{catError}</div>
                        )}

                        <div className="flex gap-2 justify-end mt-6">
                            <button onClick={() => { setCatForm(null); setCatError(""); }} disabled={busy} data-testid="adm-cat-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60">Cancel</button>
                            <button onClick={saveCategory} disabled={busy} data-testid="adm-cat-submit" className="px-4 py-2 text-xs uppercase tracking-wider bg-[#D4AF37] text-[#180A21] hover:bg-[#E8D399] disabled:opacity-50">{busy ? "Saving…" : catM.mode === "add" ? "Create category" : "Save changes"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Category delete confirm --- */}
            {catDelete && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="adm-cat-delete-modal">
                    <div className="lx-card-dark max-w-md w-full p-6">
                        <div className="lx-label text-[#D4AF37] mb-2">Confirm deletion</div>
                        <div className="font-serif text-xl text-white mb-3">Delete category “{catDelete.name}”?</div>
                        <div className="text-xs text-white/60 mb-5 leading-relaxed">
                            If this category has no services and no bookings reference it, the category is removed permanently.
                            Otherwise it (and its services) will be marked inactive so old bookings keep their original details.
                            Customers and technicians will no longer see them.
                        </div>
                        {catError && (
                            <div className="text-xs text-red-300 border border-red-500/40 bg-red-500/10 px-3 py-2 mb-4">{catError}</div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setCatDelete(null); setCatError(""); }} disabled={busy} data-testid="adm-cat-delete-cancel" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60">Cancel</button>
                            <button onClick={doDeleteCategory} disabled={busy} data-testid="adm-cat-delete-confirm" className="px-4 py-2 text-xs uppercase tracking-wider bg-red-500 text-white disabled:opacity-50">{busy ? "Deleting…" : "Delete"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
